/**
 * Gated HITL bridges for the structure set itself, like the structure set
 * menu and its settings (gear icon):
 *  - `create_structure_set`: "Create structure set" — a new, empty set on
 *    the current image that becomes the active one;
 *  - `edit_structure_set_info`: the Label and Description fields (label
 *    rules `validateStructureSetLabel`: 1–16 printable ASCII, no
 *    backslash; description ≤ 1024); refused for an approved set;
 *  - `set_structure_set_approval`: the Approval setting and its
 *    confirmation dialog. Approving sets EVERY structure to Approved and
 *    locks editing; the card shows the same warning as the dialog
 *    (structures currently rejected). The reviewer recorded is the
 *    signed-in user, as in the dialog.
 * Apply re-checks and confirms by reading the value back. Pure/React-free.
 */
import { validateStructureSetLabel } from '~/utils/validation';
import {
    asRecord,
    failedPlan,
    mutatingPreamble,
    type MutatingPlan,
    type MutatingToolCall,
    type PendingFlag,
} from '../core';
import {
    gatedChange,
    settle,
    type ChangeProposal,
} from '../core/changeProposal';
import type { StructureChangeOutcome } from '../../contract';
import type { LiveStructureSet } from '../structureManagement/liveState';

export type StructureSetProposal = ChangeProposal<StructureChangeOutcome>;
type Plan = MutatingPlan<StructureSetProposal>;

export const MAX_DESCRIPTION_LENGTH = 1024;

/** Tool status → DICOM approval status. */
export const APPROVAL: Record<string, 'APPROVED' | 'UNAPPROVED' | 'REJECTED'> =
    {
        approved: 'APPROVED',
        unapproved: 'UNAPPROVED',
        rejected: 'REJECTED',
    };

export interface StructureSetDeps extends PendingFlag {
    liveSet(): LiveStructureSet | undefined;
    currentContextId(): string | undefined;
    /** Why a new set cannot be created now (no patient / image); else undefined. */
    cannotCreateReason(): string | undefined;
    /** Name of the image a new set would belong to. */
    imageName(): string;
    createStructureSet(): void;
    setLabel(label: string): Promise<void>;
    setDescription(description: string): Promise<void>;
    setApproval(status: 'APPROVED' | 'UNAPPROVED' | 'REJECTED'): Promise<void>;
}

const APPROVED_SET =
    'the structure set is approved; unapprove it first to change it';

// ------------------------------------------------------------ create set

export async function planCreateStructureSet(
    call: MutatingToolCall,
    deps: StructureSetDeps
): Promise<Plan> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }
    const why = deps.cannotCreateReason();
    if (why) {
        return failedPlan(call.toolCallId, why);
    }
    const image = deps.imageName();
    return {
        kind: 'proposal',
        toolCallId: call.toolCallId,
        proposal: gatedChange<StructureChangeOutcome>({
            toolCallId: call.toolCallId,
            title: 'New structure set',
            summary: `create an empty structure set on ${image}`,
            details: [
                'It becomes the active structure set; the current one is ' +
                    'kept and can be selected again.',
            ],
            undoable: false,
            currentContextId: deps.currentContextId,
            recheck: deps.cannotCreateReason,
            run: async () => {
                const before = deps.currentContextId();
                deps.createStructureSet();
                await settle();
                const now = deps.liveSet();
                if (!now || now.uid === before) {
                    return {
                        status: 'failed',
                        reason: 'the viewer did not create the structure set',
                    };
                }
                return {
                    status: 'applied',
                    summary:
                        `created the empty structure set '${now.label}' ` +
                        'and made it active',
                };
            },
        }),
    };
}

// ------------------------------------------------------ label/description

export async function planEditStructureSetInfo(
    call: MutatingToolCall,
    deps: StructureSetDeps
): Promise<Plan> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }
    const o = asRecord(call.input) ?? {};
    const label = typeof o.label === 'string' ? o.label.trim() : undefined;
    const description =
        typeof o.description === 'string' ? o.description : undefined;
    if (label === undefined && description === undefined) {
        return failedPlan(
            call.toolCallId,
            'give a new label, a new description, or both'
        );
    }
    const set = deps.liveSet();
    if (!set) {
        return failedPlan(call.toolCallId, 'no structure set is loaded');
    }
    if (set.approvalStatus === 'APPROVED') {
        return failedPlan(call.toolCallId, APPROVED_SET);
    }
    if (label !== undefined) {
        const [ok, why] = validateStructureSetLabel(label);
        if (!ok) {
            return failedPlan(call.toolCallId, why ?? 'invalid label');
        }
    }
    if (
        description !== undefined &&
        description.length > MAX_DESCRIPTION_LENGTH
    ) {
        return failedPlan(
            call.toolCallId,
            `the description is longer than ${MAX_DESCRIPTION_LENGTH} characters`
        );
    }
    const changeLabel = label !== undefined && label !== set.label;
    const changeDescription =
        description !== undefined && description !== set.description;
    if (!changeLabel && !changeDescription) {
        return failedPlan(call.toolCallId, 'nothing to change');
    }
    const lines: string[] = [];
    if (changeLabel) {
        lines.push(`Label: “${set.label}” → “${label}”`);
    }
    if (changeDescription) {
        lines.push(
            description
                ? `Description: “${description}”`
                : 'Description: cleared'
        );
    }
    return {
        kind: 'proposal',
        toolCallId: call.toolCallId,
        proposal: gatedChange<StructureChangeOutcome>({
            toolCallId: call.toolCallId,
            title: 'Structure set details',
            summary: `update structure set “${set.label}”`,
            details: lines,
            undoable: false,
            currentContextId: deps.currentContextId,
            recheck: () =>
                deps.liveSet()?.approvalStatus === 'APPROVED'
                    ? APPROVED_SET
                    : undefined,
            run: async () => {
                if (changeLabel) {
                    await deps.setLabel(label as string);
                }
                if (changeDescription) {
                    await deps.setDescription(description as string);
                }
                await settle();
                const now = deps.liveSet();
                if (
                    !now ||
                    (changeLabel && now.label !== label) ||
                    (changeDescription && now.description !== description)
                ) {
                    return {
                        status: 'failed',
                        reason: 'the viewer did not update the structure set',
                    };
                }
                return {
                    status: 'applied',
                    summary: `updated the structure set: ${lines.join('; ')}`,
                };
            },
        }),
    };
}

// -------------------------------------------------------------- approval

export async function planSetStructureSetApproval(
    call: MutatingToolCall,
    deps: StructureSetDeps
): Promise<Plan> {
    const pre = mutatingPreamble(call, deps);
    if (pre !== 'ok') {
        return pre;
    }
    const raw = asRecord(call.input)?.status;
    const to = typeof raw === 'string' ? APPROVAL[raw] : undefined;
    if (!to) {
        return failedPlan(
            call.toolCallId,
            'status must be approved, unapproved or rejected'
        );
    }
    const set = deps.liveSet();
    if (!set) {
        return failedPlan(call.toolCallId, 'no structure set is loaded');
    }
    if (set.approvalStatus === to) {
        return failedPlan(
            call.toolCallId,
            `the structure set is already ${to.toLowerCase()}`
        );
    }
    const n = set.rois.length;
    // The confirmation dialog's warning: approving also approves
    // structures that are currently rejected.
    const rejected =
        to === 'APPROVED'
            ? set.rois.filter((r) => r.approvalStatus === 'REJECTED')
            : [];
    const details = [
        `All ${n} structure${n === 1 ? '' : 's'} will become ${to}.`,
        ...(rejected.length > 0
            ? [
                  `Currently rejected and will be approved too: ` +
                      rejected.map((r) => r.name).join(', '),
              ]
            : []),
        'The signed-in user is recorded as the reviewer.',
    ];
    const caution =
        to === 'APPROVED'
            ? 'Approving locks the structure set: no structure can be ' +
              'edited until it is unapproved.'
            : undefined;
    return {
        kind: 'proposal',
        toolCallId: call.toolCallId,
        proposal: gatedChange<StructureChangeOutcome>({
            toolCallId: call.toolCallId,
            title: 'Structure set approval',
            summary: `“${set.label}”: ${set.approvalStatus} → ${to}`,
            details,
            undoable: false,
            ...(caution ? { caution } : {}),
            currentContextId: deps.currentContextId,
            recheck: () =>
                deps.liveSet()?.approvalStatus === to
                    ? `the structure set is already ${to.toLowerCase()}`
                    : undefined,
            run: async () => {
                await deps.setApproval(to);
                await settle();
                if (deps.liveSet()?.approvalStatus !== to) {
                    return {
                        status: 'failed',
                        reason: 'the viewer did not change the approval status',
                    };
                }
                return {
                    status: 'applied',
                    summary:
                        `the structure set '${set.label}' and its ${n} ` +
                        `structures are now ${to}`,
                };
            },
        }),
    };
}
